import { CustomClaimValue, FacilityCountingMode } from "@app/shared";
import { useSearchParams } from "react-router-dom";
import { useHasClaim } from "../../../../hooks";

export type ParkingViewMode = {
    /** undefined while the server's answer is still loading. */
    mode: FacilityCountingMode | undefined;
    /** True when the map can't be drawn and the camera fallback should be used instead. */
    forceNoCoords: boolean;
    /** True when either value came from a URL override rather than the facility's real setup. */
    isOverridden: boolean;
};

const MODE_BY_NAME: Record<string, FacilityCountingMode> = {
    occupancy: FacilityCountingMode.Occupancy,
    inout: FacilityCountingMode.InOutDelta,
    hybrid: FacilityCountingMode.Hybrid,
    unknown: FacilityCountingMode.Unknown,
};

/**
 * Which Parking View a facility gets. Normally this is whatever the server resolved from the
 * facility's device operation modes — see FacilityProvider.GetCountingMode.
 *
 * Superadmins can override it from the URL so every variant is reachable without having to find or
 * build a facility configured that way — there are four modes and a no-coordinates fallback, and
 * most accounts will only ever have one of them in real data:
 *
 *   ?parkingView=occupancy | inout | hybrid | unknown
 *   &noCoords=1
 *
 * Hybrid is a real configuration, not a fallback: a site running a parking monitor alongside a
 * traffic counter reports both, and shows both tables.
 *
 * Gated on superadmin rather than dev-only on purpose: these states need checking against real
 * customer layouts on staging, not just against seed data. It only changes what's rendered, never
 * what's stored.
 */
export function useParkingViewMode(resolvedMode: FacilityCountingMode | undefined): ParkingViewMode {
    const [searchParams] = useSearchParams();
    const isSuperAdmin = useHasClaim(CustomClaimValue.SuperAdmin);

    const requestedMode = searchParams.get("parkingView")?.toLowerCase();
    const requestedNoCoords = searchParams.get("noCoords") === "1";

    const overrideMode = isSuperAdmin && requestedMode ? MODE_BY_NAME[requestedMode] : undefined;
    const overrideNoCoords = isSuperAdmin && requestedNoCoords;

    return {
        // undefined until the occupancy fetch lands. There is deliberately no client-side
        // fallback: the only thing to guess from would be the layout, which is what got this
        // wrong before — a site can have zones drawn and no device reporting occupancy.
        mode: overrideMode ?? resolvedMode,
        forceNoCoords: overrideNoCoords,
        isOverridden: overrideMode !== undefined || overrideNoCoords,
    };
}
